// src/data/universalIndustries.js
// COMPLETE FINAL VERSION - 40 INDUSTRIES COMBINED

// ============================================================
// INDUSTRIES 1-23 (ORIGINAL)
// ============================================================

export const UNIVERSAL_INDUSTRIES = {
  healthcare: {
    id: 'healthcare',
    name: 'Healthcare & Life Sciences',
    icon: '🏥',
    description: 'Hospitals, clinics, pharma, medical devices, biotech',
    compliance: ['HIPAA', 'HITECH', 'FDA', 'GDPR'],
    dataSensitivity: 'critical',
    subIndustries: [
      'Hospitals & Clinics', 'Pharmaceuticals', 'Medical Devices', 'Biotechnology',
      'Telemedicine', 'Mental Health', 'Dental Care', 'Veterinary Medicine',
      'Clinical Research', 'Health Insurance', 'Public Health', 'Rehabilitation',
      'Elderly Care', 'Pediatrics', 'Oncology', 'Cardiology',
      'Neurology', 'Radiology', 'Pathology', 'Emergency Medicine'
    ],
    problems: [
      {
        id: 'hc-001',
        name: 'Disease Diagnosis & Detection',
        icon: '🔬',
        description: 'AI-assisted diagnosis of diseases from medical data',
        problemTypes: ['classification', 'diagnosis', 'vision'],
        dataTypes: ['medical', 'image', 'structured'],
        priority: 1,
        complexity: 'high',
        dataVolume: 'large',
        latencyRequirement: 'standard',
        accuracyRequirement: 95,
        regulatoryCompliance: 'HIPAA',
        ethicalConsiderations: ['patient-privacy', 'bias-prevention', 'transparency'],
        roiPotential: 'high',
        useCases: [
          {
            id: 'hc-001-uc1',
            name: 'Radiology Image Analysis',
            description: 'Detect tumors, fractures, and abnormalities in X-rays, CT scans, and MRIs',
            stakeholders: ['Radiologist', 'Patient', 'Hospital Admin'],
            successCriteria: '95% accuracy in detecting abnormalities',
            components: ['GPU', 'CNN', 'Medical Imaging', 'PACS Integration']
          },
          {
            id: 'hc-001-uc2',
            name: 'Pathology Slide Analysis',
            description: 'Analyze tissue samples for cancer detection',
            stakeholders: ['Pathologist', 'Oncologist'],
            successCriteria: '90% accuracy in cancer cell detection',
            components: ['GPU', 'Vision Transformer', 'Digital Pathology']
          }
        ]
      },
      {
        id: 'hc-002',
        name: 'Medical Imaging Analysis',
        icon: '📷',
        description: 'Advanced image processing for medical diagnostics',
        problemTypes: ['vision', 'detection', 'segmentation'],
        dataTypes: ['image', 'video', 'medical'],
        priority: 1,
        complexity: 'high',
        dataVolume: 'massive',
        latencyRequirement: 'real-time',
        accuracyRequirement: 95,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-002-uc1',
            name: 'Real-time Ultrasound Analysis',
            description: 'Live analysis of ultrasound images during procedures',
            stakeholders: ['Sonographer', 'Radiologist'],
            successCriteria: 'Real-time (<100ms) image analysis',
            components: ['GPU', 'Edge AI', 'Real-time Processing']
          }
        ]
      },
      {
        id: 'hc-003',
        name: 'Drug Discovery & Development',
        icon: '💊',
        description: 'AI-powered drug discovery and clinical trial optimization',
        problemTypes: ['generation', 'prediction', 'optimization'],
        dataTypes: ['chemical', 'genomic', 'structured'],
        priority: 2,
        complexity: 'very-high',
        dataVolume: 'massive',
        latencyRequirement: 'batch',
        accuracyRequirement: 90,
        regulatoryCompliance: 'FDA',
        useCases: [
          {
            id: 'hc-003-uc1',
            name: 'Molecular Design',
            description: 'Generate novel drug candidates using generative models',
            stakeholders: ['Research Scientist', 'Pharma Company'],
            successCriteria: 'Generate 100+ viable candidates',
            components: ['GAN', 'Diffusion Model', 'Chemical Database']
          }
        ]
      },
      {
        id: 'hc-004',
        name: 'Patient Monitoring & Care',
        icon: '📊',
        description: 'Continuous patient monitoring and predictive care',
        problemTypes: ['monitoring', 'prediction', 'anomaly'],
        dataTypes: ['time-series', 'sensor', 'medical'],
        priority: 1,
        complexity: 'medium',
        dataVolume: 'large',
        latencyRequirement: 'real-time',
        accuracyRequirement: 92,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-004-uc1',
            name: 'ICU Predictive Monitoring',
            description: 'Predict patient deterioration in ICU',
            stakeholders: ['ICU Nurse', 'Intensivist'],
            successCriteria: 'Predict deterioration 4 hours early',
            components: ['LSTM', 'Time Series DB', 'Alert System']
          }
        ]
      },
      {
        id: 'hc-005',
        name: 'Administrative Automation',
        icon: '📋',
        description: 'Automate healthcare administrative tasks',
        problemTypes: ['nlp', 'extraction', 'automation'],
        dataTypes: ['document', 'text', 'structured'],
        priority: 3,
        complexity: 'low',
        dataVolume: 'medium',
        latencyRequirement: 'standard',
        accuracyRequirement: 90,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-005-uc1',
            name: 'Medical Coding Automation',
            description: 'Automatically code medical records for billing',
            stakeholders: ['Medical Coder', 'Billing Dept'],
            successCriteria: '85% coding accuracy',
            components: ['NLP', 'LLM', 'Coding Database']
          }
        ]
      },
      {
        id: 'hc-006',
        name: 'Genomics & Precision Medicine',
        icon: '🧬',
        description: 'Personalized treatment based on genetic profile',
        problemTypes: ['classification', 'prediction', 'segmentation'],
        dataTypes: ['genomic', 'medical', 'structured'],
        priority: 2,
        complexity: 'very-high',
        dataVolume: 'massive',
        latencyRequirement: 'batch',
        accuracyRequirement: 95,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-006-uc1',
            name: 'Personalized Treatment Plans',
            description: 'Tailor treatment based on genetic markers',
            stakeholders: ['Oncologist', 'Genetic Counselor'],
            successCriteria: 'Improve treatment efficacy by 30%',
            components: ['Genomic Analysis', 'ML Model', 'Treatment Database']
          }
        ]
      },
      {
        id: 'hc-007',
        name: 'Remote Patient Monitoring',
        icon: '📱',
        description: 'Monitor patients remotely using IoT devices',
        problemTypes: ['monitoring', 'anomaly', 'prediction'],
        dataTypes: ['sensor', 'time-series', 'streaming'],
        priority: 2,
        complexity: 'medium',
        dataVolume: 'large',
        latencyRequirement: 'real-time',
        accuracyRequirement: 90,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-007-uc1',
            name: 'Wearable Health Tracking',
            description: 'Track vital signs via wearables',
            stakeholders: ['Patient', 'Primary Care Physician'],
            successCriteria: 'Real-time alerts for anomalies',
            components: ['IoT Sensors', 'MQTT', 'Edge Processing']
          }
        ]
      },
      {
        id: 'hc-008',
        name: 'Clinical Trial Optimization',
        icon: '🧪',
        description: 'Optimize clinical trial design and recruitment',
        problemTypes: ['optimization', 'prediction', 'matching'],
        dataTypes: ['structured', 'document', 'medical'],
        priority: 2,
        complexity: 'high',
        dataVolume: 'large',
        latencyRequirement: 'batch',
        accuracyRequirement: 85,
        regulatoryCompliance: 'FDA',
        useCases: [
          {
            id: 'hc-008-uc1',
            name: 'Patient Recruitment',
            description: 'Identify eligible patients for trials',
            stakeholders: ['Clinical Researcher', 'Patient'],
            successCriteria: 'Reduce recruitment time by 50%',
            components: ['NLP', 'Patient Database', 'Matching Engine']
          }
        ]
      },
      {
        id: 'hc-009',
        name: 'Mental Health Analytics',
        icon: '🧠',
        description: 'AI-powered mental health assessment and support',
        problemTypes: ['sentiment', 'nlp', 'classification'],
        dataTypes: ['text', 'behavioral', 'audio'],
        priority: 3,
        complexity: 'medium',
        dataVolume: 'medium',
        latencyRequirement: 'standard',
        accuracyRequirement: 85,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-009-uc1',
            name: 'Depression Detection',
            description: 'Detect depression from text and speech patterns',
            stakeholders: ['Mental Health Professional', 'Patient'],
            successCriteria: '80% detection accuracy',
            components: ['NLP', 'Sentiment Analysis', 'Audio Processing']
          }
        ]
      },
      {
        id: 'hc-010',
        name: 'Pharmaceutical Supply Chain',
        icon: '📦',
        description: 'Optimize pharmaceutical supply chain',
        problemTypes: ['optimization', 'forecasting', 'anomaly'],
        dataTypes: ['structured', 'time-series', 'inventory'],
        priority: 3,
        complexity: 'medium',
        dataVolume: 'medium',
        latencyRequirement: 'standard',
        accuracyRequirement: 88,
        regulatoryCompliance: 'FDA',
        useCases: [
          {
            id: 'hc-010-uc1',
            name: 'Drug Demand Forecasting',
            description: 'Predict drug demand for inventory optimization',
            stakeholders: ['Supply Chain Manager', 'Pharmacist'],
            successCriteria: '90% forecast accuracy',
            components: ['Time Series', 'Forecasting Model', 'Inventory DB']
          }
        ]
      },
      {
        id: 'hc-011',
        name: 'Healthcare Fraud Detection',
        icon: '🚨',
        description: 'Detect fraudulent healthcare claims',
        problemTypes: ['anomaly', 'fraud', 'classification'],
        dataTypes: ['transaction', 'structured', 'financial'],
        priority: 2,
        complexity: 'medium',
        dataVolume: 'large',
        latencyRequirement: 'batch',
        accuracyRequirement: 90,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-011-uc1',
            name: 'Claims Fraud Detection',
            description: 'Identify fraudulent insurance claims',
            stakeholders: ['Insurance Investigator', 'Compliance Officer'],
            successCriteria: 'Detect 90% of fraudulent claims',
            components: ['Anomaly Detection', 'Graph Analysis', 'Claims DB']
          }
        ]
      },
      {
        id: 'hc-012',
        name: 'Readmission Risk Prediction',
        icon: '🔄',
        description: 'Predict hospital readmission risk',
        problemTypes: ['classification', 'prediction', 'risk'],
        dataTypes: ['structured', 'medical', 'time-series'],
        priority: 2,
        complexity: 'medium',
        dataVolume: 'large',
        latencyRequirement: 'standard',
        accuracyRequirement: 85,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-012-uc1',
            name: 'Readmission Prediction',
            description: 'Predict 30-day readmission risk',
            stakeholders: ['Care Coordinator', 'Hospital Admin'],
            successCriteria: '85% prediction accuracy',
            components: ['XGBoost', 'Patient History', 'Risk Model']
          }
        ]
      },
      {
        id: 'hc-013',
        name: 'Epidemic & Pandemic Prediction',
        icon: '🌍',
        description: 'Predict disease outbreaks and spread',
        problemTypes: ['forecasting', 'prediction', 'segmentation'],
        dataTypes: ['time-series', 'geospatial', 'structured'],
        priority: 2,
        complexity: 'high',
        dataVolume: 'massive',
        latencyRequirement: 'batch',
        accuracyRequirement: 80,
        regulatoryCompliance: 'WHO',
        useCases: [
          {
            id: 'hc-013-uc1',
            name: 'Outbreak Prediction',
            description: 'Predict disease outbreak locations',
            stakeholders: ['Public Health Official', 'Epidemiologist'],
            successCriteria: 'Predict outbreaks 2 weeks early',
            components: ['Time Series', 'Geospatial', 'Epidemiological Model']
          }
        ]
      },
      {
        id: 'hc-014',
        name: 'Personalized Treatment Plans',
        icon: '📝',
        description: 'Generate personalized treatment recommendations',
        problemTypes: ['recommendation', 'classification', 'prediction'],
        dataTypes: ['medical', 'genomic', 'structured'],
        priority: 2,
        complexity: 'high',
        dataVolume: 'large',
        latencyRequirement: 'standard',
        accuracyRequirement: 90,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-014-uc1',
            name: 'Treatment Recommendation',
            description: 'Recommend personalized treatment plans',
            stakeholders: ['Physician', 'Patient'],
            successCriteria: 'Improve outcomes by 25%',
            components: ['Recommendation Engine', 'Medical Records', 'Treatment DB']
          }
        ]
      },
      {
        id: 'hc-015',
        name: 'Medical Chatbot & Triage',
        icon: '💬',
        description: 'AI chatbot for patient triage and support',
        problemTypes: ['chatbot', 'nlp', 'classification'],
        dataTypes: ['text', 'conversational', 'medical'],
        priority: 3,
        complexity: 'medium',
        dataVolume: 'medium',
        latencyRequirement: 'real-time',
        accuracyRequirement: 85,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-015-uc1',
            name: 'Symptom Checker',
            description: 'AI-powered symptom assessment and triage',
            stakeholders: ['Patient', 'Nurse'],
            successCriteria: '85% triage accuracy',
            components: ['Chatbot', 'NLP', 'Medical Knowledge Base']
          }
        ]
      },
      {
        id: 'hc-016',
        name: 'Surgical Planning & Assistance',
        icon: '🔪',
        description: 'AI-assisted surgical planning',
        problemTypes: ['vision', 'segmentation', 'planning'],
        dataTypes: ['image', '3d', 'medical'],
        priority: 2,
        complexity: 'very-high',
        dataVolume: 'large',
        latencyRequirement: 'real-time',
        accuracyRequirement: 95,
        regulatoryCompliance: 'FDA',
        useCases: [
          {
            id: 'hc-016-uc1',
            name: 'Surgical Planning',
            description: '3D surgical planning from imaging',
            stakeholders: ['Surgeon', 'Patient'],
            successCriteria: 'Improve surgical precision by 30%',
            components: ['3D Vision', 'Segmentation', 'Planning Engine']
          }
        ]
      },
      {
        id: 'hc-017',
        name: 'Vital Signs Prediction',
        icon: '❤️',
        description: 'Predict vital signs deterioration',
        problemTypes: ['prediction', 'forecasting', 'anomaly'],
        dataTypes: ['time-series', 'sensor', 'medical'],
        priority: 2,
        complexity: 'medium',
        dataVolume: 'large',
        latencyRequirement: 'real-time',
        accuracyRequirement: 90,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-017-uc1',
            name: 'Vital Signs Prediction',
            description: 'Predict patient vital signs',
            stakeholders: ['Nurse', 'Physician'],
            successCriteria: '90% prediction accuracy',
            components: ['LSTM', 'Time Series', 'Patient Monitor']
          }
        ]
      },
      {
        id: 'hc-018',
        name: 'Lab Result Analysis',
        icon: '🧪',
        description: 'Automated lab result interpretation',
        problemTypes: ['classification', 'extraction', 'analysis'],
        dataTypes: ['structured', 'medical', 'document'],
        priority: 3,
        complexity: 'low',
        dataVolume: 'medium',
        latencyRequirement: 'standard',
        accuracyRequirement: 92,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-018-uc1',
            name: 'Lab Result Interpretation',
            description: 'Automatically interpret lab results',
            stakeholders: ['Lab Technician', 'Physician'],
            successCriteria: '92% interpretation accuracy',
            components: ['NLP', 'Classification', 'Lab Database']
          }
        ]
      },
      {
        id: 'hc-019',
        name: 'Prescription Optimization',
        icon: '💊',
        description: 'Optimize medication prescriptions',
        problemTypes: ['recommendation', 'optimization', 'prediction'],
        dataTypes: ['structured', 'medical', 'genomic'],
        priority: 2,
        complexity: 'medium',
        dataVolume: 'medium',
        latencyRequirement: 'standard',
        accuracyRequirement: 88,
        regulatoryCompliance: 'FDA',
        useCases: [
          {
            id: 'hc-019-uc1',
            name: 'Prescription Recommendation',
            description: 'Recommend optimal medication',
            stakeholders: ['Physician', 'Pharmacist'],
            successCriteria: 'Reduce adverse events by 40%',
            components: ['Recommendation Engine', 'Drug Database', 'Patient History']
          }
        ]
      },
      {
        id: 'hc-020',
        name: 'Hospital Resource Optimization',
        icon: '🏥',
        description: 'Optimize hospital resource allocation',
        problemTypes: ['optimization', 'scheduling', 'forecasting'],
        dataTypes: ['structured', 'time-series', 'inventory'],
        priority: 2,
        complexity: 'medium',
        dataVolume: 'medium',
        latencyRequirement: 'standard',
        accuracyRequirement: 85,
        regulatoryCompliance: 'HIPAA',
        useCases: [
          {
            id: 'hc-020-uc1',
            name: 'Bed Allocation',
            description: 'Optimize hospital bed allocation',
            stakeholders: ['Hospital Admin', 'Nurse Manager'],
            successCriteria: 'Reduce wait time by 40%',
            components: ['Optimization Engine', 'Bed Management', 'Forecasting']
          }
        ]
      }
    ]
  },

  finance: {
    id: 'finance',
    name: 'Financial Services & Banking',
    icon: '🏦',
    description: 'Banking, insurance, trading, wealth management',
    compliance: ['SOC2', 'PCI-DSS', 'GDPR', 'FINRA'],
    dataSensitivity: 'critical',
    subIndustries: [
      'Retail Banking', 'Investment Banking', 'Wealth Management', 'Insurance',
      'Credit Cards', 'Mortgage Lending', 'Payment Processing', 'Capital Markets',
      'Private Equity', 'Hedge Funds', 'FinTech', 'Credit Unions',
      'Microfinance', 'Asset Management', 'Trading'
    ],
    problems: [
      {
        id: 'fin-001',
        name: 'Fraud Detection & Prevention',
        icon: '🚨',
        description: 'Detect and prevent financial fraud',
        problemTypes: ['anomaly', 'fraud', 'classification'],
        dataTypes: ['transaction', 'financial', 'streaming'],
        priority: 1,
        complexity: 'high',
        dataVolume: 'massive',
        latencyRequirement: 'real-time',
        accuracyRequirement: 95,
        regulatoryCompliance: 'PCI-DSS',
        useCases: [
          {
            id: 'fin-001-uc1',
            name: 'Credit Card Fraud Detection',
            description: 'Real-time credit card fraud detection',
            stakeholders: ['Fraud Analyst', 'Customer'],
            successCriteria: '95% fraud detection rate',
            components: ['Real-time Processing', 'Anomaly Detection', 'Transaction DB']
          },
          {
            id: 'fin-001-uc2',
            name: 'Identity Theft Prevention',
            description: 'Detect identity theft attempts',
            stakeholders: ['Security Team', 'Customer'],
            successCriteria: '90% detection accuracy',
            components: ['Graph Analysis', 'ML Model', 'Identity Database']
          }
        ]
      },
      {
        id: 'fin-002',
        name: 'Risk Assessment & Scoring',
        icon: '📊',
        description: 'Credit risk assessment and scoring',
        problemTypes: ['classification', 'risk', 'scoring'],
        dataTypes: ['financial', 'structured', 'customer'],
        priority: 1,
        complexity: 'medium',
        dataVolume: 'large',
        latencyRequirement: 'standard',
        accuracyRequirement: 90,
        regulatoryCompliance: 'SOC2',
        useCases: [
          {
            id: 'fin-002-uc1',
            name: 'Credit Scoring',
            description: 'Predict creditworthiness',
            stakeholders: ['Loan Officer', 'Customer'],
            successCriteria: '90% scoring accuracy',
            components: ['XGBoost', 'Credit History', 'Risk Model']
          }
        ]
      },
      {
        id: 'fin-003',
        name: 'Algorithmic Trading',
        icon: '📈',
        description: 'AI-powered trading strategies',
        problemTypes: ['prediction', 'optimization', 'reinforcement'],
        dataTypes: ['market', 'time-series', 'streaming'],
        priority: 2,
        complexity: 'very-high',
        dataVolume: 'massive',
        latencyRequirement: 'ultra-low',
        accuracyRequirement: 85,
        regulatoryCompliance: 'FINRA',
        useCases: [
          {
            id: 'fin-003-uc1',
            name: 'Trade Execution',
            description: 'AI-powered trade execution',
            stakeholders: ['Trader', 'Quant Analyst'],
            successCriteria: 'Improve execution by 20%',
            components: ['Reinforcement Learning', 'Market Data', 'Execution Engine']
          }
        ]
      },
      {
        id: 'fin-004',
        name: 'Customer Churn Prediction',
        icon: '👋',
        description: 'Predict customer churn for retention',
        problemTypes: ['classification', 'prediction', 'churn'],
        dataTypes: ['customer', 'behavioral', 'structured'],
        priority: 2,
        complexity: 'medium',
        dataVolume: 'large',
        latencyRequirement: 'standard',
        accuracyRequirement: 85,
        regulatoryCompliance: 'SOC2',
        useCases: [
          {
            id: 'fin-004-uc1',
            name: 'Churn Prediction',
            description: 'Predict customer churn',
            stakeholders: ['Customer Success', 'Marketing'],
            successCriteria: '85% churn prediction accuracy',
            components: ['Classification', 'Customer Data', 'Prediction Model']
          }
        ]
      },
      {
        id: 'fin-005',
        name: 'Anti-Money Laundering (AML)',
        icon: '💰',
        description: 'Detect money laundering activities',
        problemTypes: ['anomaly', 'detection', 'graph'],
        dataTypes: ['transaction', 'graph', 'financial'],
        priority: 1,
        complexity: 'high',
        dataVolume: 'massive',
        latencyRequirement: 'real-time',
        accuracyRequirement: 92,
        regulatoryCompliance: 'AML',
        useCases: [
          {
            id: 'fin-005-uc1',
            name: 'AML Detection',
            description: 'Detect suspicious transactions',
            stakeholders: ['Compliance Officer', 'Regulator'],
            successCriteria: '92% detection rate',
            components: ['Graph Analysis', 'Anomaly Detection', 'Transaction Monitoring']
          }
        ]
      },
      {
        id: 'fin-006',
        name: 'Loan Underwriting',
        icon: '📝',
        description: 'Automated loan approval',
        problemTypes: ['classification', 'risk', 'prediction'],
        dataTypes: ['financial', 'structured', 'document'],
        priority: 2,
        complexity: 'medium',
        dataVolume: 'large',
        latencyRequirement: 'standard',
        accuracyRequirement: 90,
        regulatoryCompliance: 'SOC2',
        useCases: [
          {
            id: 'fin-006-uc1',
            name: 'Auto Loan Approval',
            description: 'Automated loan approval',
            stakeholders: ['Loan Officer', 'Customer'],
            successCriteria: '90% approval accuracy',
            components: ['ML Model', 'Credit Data', 'Risk Scoring']
          }
        ]
      },
      {
        id: 'fin-007',
        name: 'Investment Portfolio Optimization',
        icon: '📊',
        description: 'Optimize investment portfolios',
        problemTypes: ['optimization', 'recommendation', 'prediction'],
        dataTypes: ['market', 'financial', 'structured'],
        priority: 2,
        complexity: 'high',
        dataVolume: 'large',
        latencyRequirement: 'standard',
        accuracyRequirement: 85,
        regulatoryCompliance: 'FINRA',
        useCases: [
          {
            id: 'fin-007-uc1',
            name: 'Portfolio Optimization',
            description: 'Optimize asset allocation',
            stakeholders: ['Portfolio Manager', 'Investor'],
            successCriteria: 'Improve returns by 15%',
            components: ['Optimization Engine', 'Market Data', 'Risk Model']
          }
        ]
      },
      {
        id: 'fin-008',
        name: 'Insurance Claims Processing',
        icon: '📋',
        description: 'Automate insurance claims processing',
        problemTypes: ['nlp', 'extraction', 'classification'],
        dataTypes: ['document', 'text', 'structured'],
        priority: 2,
        complexity: 'medium',
        dataVolume: 'large',
        latencyRequirement: 'standard',
        accuracyRequirement: 90,
        regulatoryCompliance: 'SOC2',
        useCases: [
          {
            id: 'fin-008-uc1',
            name: 'Claims Automation',
            description: 'Automate claims processing',
            stakeholders: ['Claims Adjuster', 'Customer'],
            successCriteria: 'Reduce processing time by 60%',
            components: ['NLP', 'Document Processing', 'Claims Database']
          }
        ]
      },
      {
        id: 'fin-009',
        name: 'Customer Service Chatbot',
        icon: '💬',
        description: 'AI chatbot for customer support',
        problemTypes: ['chatbot', 'nlp', 'conversational'],
        dataTypes: ['text', 'conversational', 'structured'],
        priority: 3,
        complexity: 'low',
        dataVolume: 'medium',
        latencyRequirement: 'real-time',
        accuracyRequirement: 85,
        regulatoryCompliance: 'SOC2',
        useCases: [
          {
            id: 'fin-009-uc1',
            name: 'Banking Chatbot',
            description: 'Answer customer queries',
            stakeholders: ['Customer', 'Support Team'],
            successCriteria: 'Resolve 70% of queries',
            components: ['Chatbot', 'NLP', 'Knowledge Base']
          }
        ]
      },
      {
        id: 'fin-010',
        name: 'Market Sentiment Analysis',
        icon: '📰',
        description: 'Analyze market sentiment from news',
        problemTypes: ['sentiment', 'nlp', 'analysis'],
        dataTypes: ['text', 'social', 'market'],
        priority: 2,
        complexity: 'medium',
        dataVolume: 'large',
        latencyRequirement: 'real-time',
        accuracyRequirement: 85,
        regulatoryCompliance: 'FINRA',
        useCases: [
          {
            id: 'fin-010-uc1',
            name: 'Sentiment Analysis',
            description: 'Analyze market sentiment',
            stakeholders: ['Trader', 'Analyst'],
            successCriteria: '85% sentiment accuracy',
            components: ['NLP', 'Sentiment Analysis', 'News Feed']
          }
        ]
      }
    ]
  },

  // ============================================================
  // INDUSTRIES 3-23 (ABBREVIATED - All key problems with use cases)
  // ============================================================

  retail: {
    id: 'retail',
    name: 'Retail & E-Commerce',
    icon: '🛒',
    description: 'Online and brick-and-mortar retail',
    compliance: ['PCI-DSS', 'GDPR'],
    dataSensitivity: 'sensitive',
    subIndustries: ['E-Commerce', 'Brick & Mortar', 'Grocery', 'Fashion', 'Electronics', 'Home Goods', 'Luxury', 'Sports', 'Beauty', 'Pharmacy Retail', 'Department', 'Specialty', 'Warehouse', 'D2C', 'Subscription'],
    problems: [
      {
        id: 'ret-001',
        name: 'Product Recommendation',
        icon: '🎯',
        problemTypes: ['recommendation', 'personalization', 'ranking'],
        dataTypes: ['customer', 'behavioral', 'transaction'],
        priority: 1,
        useCases: [
          { id: 'ret-001-uc1', name: 'Product Recommendations', description: 'Recommend products to shoppers', stakeholders: ['Customer', 'Marketing'], successCriteria: 'Increase conversion by 20%', components: ['Recommendation Engine', 'User Behavior', 'Product Catalog'] },
          { id: 'ret-001-uc2', name: 'Cross-sell Recommendations', description: 'Recommend complementary products', stakeholders: ['Customer', 'Sales'], successCriteria: 'Increase AOV by 15%', components: ['Collaborative Filtering', 'Purchase History', 'Product DB'] }
        ]
      },
      {
        id: 'ret-002',
        name: 'Demand Forecasting',
        icon: '📈',
        problemTypes: ['forecasting', 'prediction', 'time-series'],
        dataTypes: ['transaction', 'time-series', 'inventory'],
        priority: 1,
        useCases: [
          { id: 'ret-002-uc1', name: 'Sales Forecasting', description: 'Predict sales for inventory', stakeholders: ['Inventory Manager', 'Buyer'], successCriteria: '90% forecast accuracy', components: ['Time Series', 'Forecasting Model', 'Sales Data'] }
        ]
      },
      {
        id: 'ret-003',
        name: 'Inventory Optimization',
        icon: '📦',
        problemTypes: ['optimization', 'forecasting', 'prediction'],
        dataTypes: ['inventory', 'time-series', 'structured'],
        priority: 1,
        useCases: [
          { id: 'ret-003-uc1', name: 'Stock Optimization', description: 'Optimize stock levels', stakeholders: ['Supply Chain', 'Store Manager'], successCriteria: 'Reduce stockouts by 50%', components: ['Optimization Engine', 'Inventory Data', 'Forecasting'] }
        ]
      },
      {
        id: 'ret-004',
        name: 'Price Optimization',
        icon: '💰',
        problemTypes: ['optimization', 'prediction', 'regression'],
        dataTypes: ['transaction', 'market', 'competitor'],
        priority: 2,
        useCases: [
          { id: 'ret-004-uc1', name: 'Dynamic Pricing', description: 'Adjust prices in real-time', stakeholders: ['Pricing Manager', 'Customer'], successCriteria: 'Increase margin by 10%', components: ['Optimization', 'Competitor Data', 'Pricing Engine'] }
        ]
      },
      {
        id: 'ret-005',
        name: 'Customer Segmentation',
        icon: '👥',
        problemTypes: ['segmentation', 'clustering', 'classification'],
        dataTypes: ['customer', 'behavioral', 'transaction'],
        priority: 2,
        useCases: [
          { id: 'ret-005-uc1', name: 'Customer Segmentation', description: 'Segment customers for targeting', stakeholders: ['Marketing', 'CRM'], successCriteria: 'Improve campaign ROI by 30%', components: ['Clustering', 'Customer Data', 'Segmentation Engine'] }
        ]
      },
      {
        id: 'ret-006',
        name: 'Visual Search',
        icon: '🔍',
        problemTypes: ['vision', 'search', 'matching'],
        dataTypes: ['image', 'product', 'visual'],
        priority: 3,
        useCases: [
          { id: 'ret-006-uc1', name: 'Visual Product Search', description: 'Search products by image', stakeholders: ['Customer', 'E-commerce Team'], successCriteria: '85% search accuracy', components: ['CNN', 'Image Search', 'Product Catalog'] }
        ]
      },
      {
        id: 'ret-007',
        name: 'Chatbot for Shopping',
        icon: '💬',
        problemTypes: ['chatbot', 'nlp', 'recommendation'],
        dataTypes: ['text', 'conversational', 'product'],
        priority: 2,
        useCases: [
          { id: 'ret-007-uc1', name: 'Shopping Assistant', description: 'AI shopping assistant', stakeholders: ['Customer', 'Support'], successCriteria: 'Resolve 60% of queries', components: ['Chatbot', 'NLP', 'Product Database'] }
        ]
      },
      {
        id: 'ret-008',
        name: 'Fraud Detection',
        icon: '🚨',
        problemTypes: ['anomaly', 'fraud', 'classification'],
        dataTypes: ['transaction', 'behavioral', 'financial'],
        priority: 1,
        useCases: [
          { id: 'ret-008-uc1', name: 'Order Fraud Detection', description: 'Detect fraudulent orders', stakeholders: ['Fraud Team', 'Customer'], successCriteria: '92% detection rate', components: ['Anomaly Detection', 'Transaction Analysis', 'Fraud Model'] }
        ]
      },
      {
        id: 'ret-009',
        name: 'Supply Chain Optimization',
        icon: '🚚',
        problemTypes: ['optimization', 'forecasting', 'routing'],
        dataTypes: ['inventory', 'logistics', 'time-series'],
        priority: 2,
        useCases: [
          { id: 'ret-009-uc1', name: 'Route Optimization', description: 'Optimize delivery routes', stakeholders: ['Logistics Manager', 'Driver'], successCriteria: 'Reduce delivery cost by 20%', components: ['Routing Engine', 'Logistics Data', 'Optimization'] }
        ]
      },
      {
        id: 'ret-010',
        name: 'Churn Prediction',
        icon: '👋',
        problemTypes: ['classification', 'churn', 'prediction'],
        dataTypes: ['customer', 'behavioral', 'transaction'],
        priority: 2,
        useCases: [
          { id: 'ret-010-uc1', name: 'Customer Churn', description: 'Predict customer churn', stakeholders: ['CRM', 'Marketing'], successCriteria: '85% accuracy', components: ['Classification', 'Customer History', 'Churn Model'] }
        ]
      }
    ]
  },

  manufacturing: {
    id: 'manufacturing',
    name: 'Manufacturing & Industrial',
    icon: '🏭',
    description: 'Production, quality, maintenance',
    compliance: ['ISO', 'OSHA', 'IEC'],
    dataSensitivity: 'internal',
    subIndustries: ['Automotive', 'Electronics', 'Consumer Goods', 'Heavy Machinery', 'Textiles', 'Food Processing', 'Chemical', 'Plastics', 'Metal Fabrication', 'Semiconductor', 'Aerospace Components', 'Medical Devices', 'Furniture', 'Packaging', 'Industrial Equipment'],
    problems: [
      {
        id: 'mfg-001',
        name: 'Predictive Maintenance',
        icon: '🔧',
        problemTypes: ['prediction', 'anomaly', 'maintenance'],
        dataTypes: ['sensor', 'time-series', 'equipment'],
        priority: 1,
        useCases: [
          { id: 'mfg-001-uc1', name: 'Machine Failure Prediction', description: 'Predict machine failures', stakeholders: ['Maintenance Engineer', 'Plant Manager'], successCriteria: 'Predict failures 48 hours early', components: ['IoT Sensors', 'Predictive Model', 'Alert System'] }
        ]
      },
      {
        id: 'mfg-002',
        name: 'Quality Control & Inspection',
        icon: '✅',
        problemTypes: ['vision', 'detection', 'quality'],
        dataTypes: ['image', 'video', 'sensor'],
        priority: 1,
        useCases: [
          { id: 'mfg-002-uc1', name: 'Visual Inspection', description: 'Detect product defects', stakeholders: ['Quality Engineer', 'Production'], successCriteria: '95% defect detection', components: ['CNN', 'Camera', 'Quality Model'] }
        ]
      },
      {
        id: 'mfg-003',
        name: 'Production Optimization',
        icon: '⚙️',
        problemTypes: ['optimization', 'scheduling', 'prediction'],
        dataTypes: ['sensor', 'time-series', 'process'],
        priority: 2,
        useCases: [
          { id: 'mfg-003-uc1', name: 'Production Scheduling', description: 'Optimize production schedule', stakeholders: ['Production Manager', 'Planner'], successCriteria: 'Increase throughput by 20%', components: ['Optimization Engine', 'Production Data', 'Scheduling'] }
        ]
      },
      {
        id: 'mfg-004',
        name: 'Supply Chain Optimization',
        icon: '🚚',
        problemTypes: ['optimization', 'forecasting', 'routing'],
        dataTypes: ['inventory', 'logistics', 'time-series'],
        priority: 2,
        useCases: [
          { id: 'mfg-004-uc1', name: 'Supplier Optimization', description: 'Optimize supplier selection', stakeholders: ['Supply Chain Manager'], successCriteria: 'Reduce cost by 15%', components: ['Optimization', 'Supplier Data', 'Cost Model'] }
        ]
      },
      {
        id: 'mfg-005',
        name: 'Energy Consumption Optimization',
        icon: '⚡',
        problemTypes: ['optimization', 'forecasting', 'anomaly'],
        dataTypes: ['energy', 'sensor', 'time-series'],
        priority: 3,
        useCases: [
          { id: 'mfg-005-uc1', name: 'Energy Optimization', description: 'Reduce energy consumption', stakeholders: ['Facility Manager'], successCriteria: 'Reduce energy by 20%', components: ['Optimization', 'Energy Monitor', 'Forecasting'] }
        ]
      },
      {
        id: 'mfg-006',
        name: 'Digital Twin Simulation',
        icon: '🔄',
        problemTypes: ['simulation', 'prediction', 'optimization'],
        dataTypes: ['sensor', '3d', 'time-series'],
        priority: 2,
        useCases: [
          { id: 'mfg-006-uc1', name: 'Digital Twin', description: 'Create digital twin of production line', stakeholders: ['Plant Manager', 'Engineer'], successCriteria: '90% simulation accuracy', components: ['3D Modeling', 'Real-time Data', 'Simulation Engine'] }
        ]
      },
      {
        id: 'mfg-007',
        name: 'Anomaly Detection in Production',
        icon: '⚠️',
        problemTypes: ['anomaly', 'detection', 'monitoring'],
        dataTypes: ['sensor', 'streaming', 'time-series'],
        priority: 1,
        useCases: [
          { id: 'mfg-007-uc1', name: 'Process Anomaly Detection', description: 'Detect process anomalies', stakeholders: ['Process Engineer', 'Operator'], successCriteria: '92% detection rate', components: ['Anomaly Detection', 'Sensor Data', 'Alert System'] }
        ]
      },
      {
        id: 'mfg-008',
        name: 'Inventory Optimization',
        icon: '📦',
        problemTypes: ['optimization', 'forecasting', 'prediction'],
        dataTypes: ['inventory', 'time-series', 'structured'],
        priority: 2,
        useCases: [
          { id: 'mfg-008-uc1', name: 'Inventory Optimization', description: 'Optimize inventory levels', stakeholders: ['Inventory Manager'], successCriteria: 'Reduce holding cost by 25%', components: ['Optimization', 'Inventory Data', 'Forecasting'] }
        ]
      }
    ]
  },

  // ============================================================
  // REMAINING INDUSTRIES 5-40 (Complete with key problems)
  // ============================================================

  technology: {
    id: 'technology',
    name: 'Technology & Software',
    icon: '💻',
    description: 'SaaS, cloud, cybersecurity, IT services',
    compliance: ['SOC2', 'ISO 27001', 'GDPR'],
    dataSensitivity: 'sensitive',
    subIndustries: ['SaaS', 'Cloud Services', 'Cybersecurity', 'IT Consulting', 'Software Development', 'Data Analytics', 'AI/ML Platforms', 'DevOps Tools', 'Enterprise Software', 'Mobile Apps', 'Web Development', 'Blockchain', 'IoT Platforms', 'Gaming', 'AR/VR'],
    problems: [
      {
        id: 'tech-001',
        name: 'Code Generation & Assistance',
        icon: '💻',
        problemTypes: ['generation', 'nlp', 'automation'],
        dataTypes: ['text', 'code', 'structured'],
        priority: 1,
        useCases: [
          { id: 'tech-001-uc1', name: 'Code Completion', description: 'AI code completion', stakeholders: ['Developer', 'Engineering Manager'], successCriteria: 'Increase productivity by 30%', components: ['LLM', 'Code Database', 'IDE Integration'] }
        ]
      },
      {
        id: 'tech-002',
        name: 'Bug Detection & Fixing',
        icon: '🐛',
        problemTypes: ['detection', 'classification', 'generation'],
        dataTypes: ['code', 'log', 'text'],
        priority: 1,
        useCases: [
          { id: 'tech-002-uc1', name: 'Bug Detection', description: 'Detect code bugs', stakeholders: ['Developer', 'QA Engineer'], successCriteria: '85% bug detection', components: ['Static Analysis', 'ML Model', 'Code Database'] }
        ]
      },
      {
        id: 'tech-003',
        name: 'Cybersecurity Threat Detection',
        icon: '🛡️',
        problemTypes: ['anomaly', 'detection', 'classification'],
        dataTypes: ['log', 'network', 'streaming'],
        priority: 1,
        useCases: [
          { id: 'tech-003-uc1', name: 'Threat Detection', description: 'Detect security threats', stakeholders: ['Security Analyst', 'CISO'], successCriteria: '95% detection rate', components: ['Anomaly Detection', 'Network Analysis', 'Alert System'] }
        ]
      },
      {
        id: 'tech-004',
        name: 'Customer Support Automation',
        icon: '💬',
        problemTypes: ['chatbot', 'nlp', 'automation'],
        dataTypes: ['text', 'conversational', 'ticket'],
        priority: 2,
        useCases: [
          { id: 'tech-004-uc1', name: 'Support Chatbot', description: 'Automate support tickets', stakeholders: ['Support Team', 'Customer'], successCriteria: 'Resolve 70% of tickets', components: ['Chatbot', 'NLP', 'Ticket System'] }
        ]
      },
      {
        id: 'tech-005',
        name: 'Log Analysis & Monitoring',
        icon: '📊',
        problemTypes: ['anomaly', 'detection', 'analysis'],
        dataTypes: ['log', 'time-series', 'streaming'],
        priority: 2,
        useCases: [
          { id: 'tech-005-uc1', name: 'Log Analysis', description: 'Analyze system logs', stakeholders: ['DevOps', 'SRE'], successCriteria: '90% anomaly detection', components: ['ELK Stack', 'Anomaly Detection', 'Monitoring'] }
        ]
      }
    ]
  },

  energy: {
    id: 'energy',
    name: 'Energy & Utilities',
    icon: '⚡',
    description: 'Oil, gas, renewable, utilities',
    compliance: ['EPA', 'NERC', 'FERC'],
    dataSensitivity: 'internal',
    subIndustries: ['Oil & Gas', 'Renewable Energy', 'Solar', 'Wind', 'Hydroelectric', 'Nuclear', 'Grid Management', 'Smart Meters', 'Energy Storage', 'Utilities', 'Pipeline', 'Geothermal', 'Biomass', 'Tidal', 'Electric Vehicles'],
    problems: [
      {
        id: 'nrg-001',
        name: 'Energy Demand Forecasting',
        icon: '📈',
        problemTypes: ['forecasting', 'prediction', 'time-series'],
        dataTypes: ['time-series', 'energy', 'weather'],
        priority: 1,
        useCases: [
          { id: 'nrg-001-uc1', name: 'Demand Forecasting', description: 'Predict energy demand', stakeholders: ['Grid Operator', 'Utility Company'], successCriteria: '90% forecast accuracy', components: ['Time Series', 'Forecasting Model', 'Weather Data'] }
        ]
      },
      {
        id: 'nrg-002',
        name: 'Grid Optimization',
        icon: '🔌',
        problemTypes: ['optimization', 'scheduling', 'prediction'],
        dataTypes: ['energy', 'time-series', 'sensor'],
        priority: 1,
        useCases: [
          { id: 'nrg-002-uc1', name: 'Grid Optimization', description: 'Optimize power distribution', stakeholders: ['Grid Operator'], successCriteria: 'Reduce losses by 15%', components: ['Optimization Engine', 'Grid Data', 'Real-time Monitoring'] }
        ]
      },
      {
        id: 'nrg-003',
        name: 'Predictive Maintenance',
        icon: '🔧',
        problemTypes: ['prediction', 'anomaly', 'maintenance'],
        dataTypes: ['sensor', 'time-series', 'equipment'],
        priority: 2,
        useCases: [
          { id: 'nrg-003-uc1', name: 'Turbine Maintenance', description: 'Predict turbine failures', stakeholders: ['Maintenance Engineer'], successCriteria: 'Predict 48 hours early', components: ['IoT Sensors', 'Predictive Model', 'Alert System'] }
        ]
      },
      {
        id: 'nrg-004',
        name: 'Renewable Energy Optimization',
        icon: '☀️',
        problemTypes: ['optimization', 'forecasting', 'prediction'],
        dataTypes: ['weather', 'energy', 'time-series'],
        priority: 2,
        useCases: [
          { id: 'nrg-004-uc1', name: 'Solar Optimization', description: 'Optimize solar energy', stakeholders: ['Energy Manager'], successCriteria: 'Increase output by 20%', components: ['Optimization', 'Weather Data', 'Solar Panels'] }
        ]
      }
    ]
  },

  transport: {
    id: 'transport',
    name: 'Transportation & Logistics',
    icon: '🚚',
    description: 'Logistics, fleet management, autonomous',
    compliance: ['DOT', 'FMCSA'],
    dataSensitivity: 'internal',
    subIndustries: ['Trucking', 'Shipping', 'Air Freight', 'Rail', 'Last Mile', 'Fleet Management', 'Warehousing', 'Port Operations', 'Customs', '3PL'],
    problems: [
      { id: 'trn-001', name: 'Route Optimization', icon: '🗺️', problemTypes: ['optimization', 'routing'], dataTypes: ['geospatial', 'time-series'], priority: 1 },
      { id: 'trn-002', name: 'Fleet Management', icon: '🚛', problemTypes: ['monitoring', 'optimization'], dataTypes: ['sensor', 'gps'], priority: 1 },
      { id: 'trn-003', name: 'Demand Prediction', icon: '📈', problemTypes: ['forecasting', 'prediction'], dataTypes: ['time-series', 'structured'], priority: 2 },
      { id: 'trn-004', name: 'Autonomous Vehicles', icon: '🚗', problemTypes: ['vision', 'reinforcement'], dataTypes: ['video', 'sensor'], priority: 2 },
      { id: 'trn-005', name: 'Predictive Maintenance', icon: '🔧', problemTypes: ['prediction', 'anomaly'], dataTypes: ['sensor', 'time-series'], priority: 1 }
    ]
  },

  agriculture: {
    id: 'agriculture',
    name: 'Agriculture & Food',
    icon: '🌾',
    description: 'Farming, livestock, irrigation',
    compliance: ['USDA', 'FDA'],
    dataSensitivity: 'internal',
    subIndustries: ['Crop Farming', 'Livestock', 'Aquaculture', 'Greenhouse', 'Organic', 'Irrigation', 'Food Processing', 'Dairy', 'Poultry', 'Viticulture'],
    problems: [
      { id: 'agr-001', name: 'Crop Yield Prediction', icon: '🌾', problemTypes: ['prediction', 'forecasting'], dataTypes: ['satellite', 'weather'], priority: 1 },
      { id: 'agr-002', name: 'Pest Detection', icon: '🐛', problemTypes: ['detection', 'vision'], dataTypes: ['image', 'sensor'], priority: 1 },
      { id: 'agr-003', name: 'Irrigation Optimization', icon: '💧', problemTypes: ['optimization'], dataTypes: ['sensor', 'weather'], priority: 2 },
      { id: 'agr-004', name: 'Soil Analysis', icon: '🌱', problemTypes: ['classification', 'prediction'], dataTypes: ['sensor', 'chemical'], priority: 2 },
      { id: 'agr-005', name: 'Livestock Monitoring', icon: '🐄', problemTypes: ['monitoring', 'anomaly'], dataTypes: ['sensor', 'video'], priority: 1 }
    ]
  },

  education: {
    id: 'education',
    name: 'Education & Training',
    icon: '📚',
    description: 'K-12, higher ed, corporate training',
    compliance: ['FERPA', 'COPPA'],
    dataSensitivity: 'sensitive',
    subIndustries: ['K-12', 'Higher Education', 'Corporate Training', 'Online Learning', 'EdTech', 'Vocational', 'Language Learning', 'Test Prep', 'Special Education', 'STEM Education'],
    problems: [
      { id: 'edu-001', name: 'Personalized Learning', icon: '🎯', problemTypes: ['recommendation', 'personalization'], dataTypes: ['behavioral', 'structured'], priority: 1 },
      { id: 'edu-002', name: 'Student Performance Prediction', icon: '📊', problemTypes: ['prediction', 'classification'], dataTypes: ['structured', 'behavioral'], priority: 2 },
      { id: 'edu-003', name: 'Automated Grading', icon: '✅', problemTypes: ['nlp', 'classification'], dataTypes: ['text', 'document'], priority: 2 },
      { id: 'edu-004', name: 'Chatbot for Learning', icon: '💬', problemTypes: ['chatbot', 'nlp'], dataTypes: ['text', 'conversational'], priority: 3 },
      { id: 'edu-005', name: 'Dropout Prevention', icon: '⚠️', problemTypes: ['classification', 'prediction'], dataTypes: ['behavioral', 'structured'], priority: 2 }
    ]
  },

  telecom: {
    id: 'telecom',
    name: 'Telecommunications',
    icon: '📡',
    description: 'Networks, 5G, IoT connectivity',
    compliance: ['FCC', 'GDPR'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Mobile', 'ISP', '5G', 'Satellite', 'Fiber', 'VoIP', 'Network Equipment', 'Tower', 'Broadband', 'MVNO'],
    problems: [
      { id: 'tel-001', name: 'Network Optimization', icon: '📡', problemTypes: ['optimization', 'prediction'], dataTypes: ['network', 'time-series'], priority: 1 },
      { id: 'tel-002', name: 'Churn Prediction', icon: '👋', problemTypes: ['classification', 'churn'], dataTypes: ['customer', 'behavioral'], priority: 1 },
      { id: 'tel-003', name: 'Network Anomaly Detection', icon: '⚠️', problemTypes: ['anomaly', 'detection'], dataTypes: ['network', 'streaming'], priority: 1 },
      { id: 'tel-004', name: 'Customer Service Chatbot', icon: '💬', problemTypes: ['chatbot', 'nlp'], dataTypes: ['text', 'conversational'], priority: 2 }
    ]
  },

  media: {
    id: 'media',
    name: 'Media & Entertainment',
    icon: '🎬',
    description: 'Streaming, content, publishing',
    compliance: ['DMCA', 'GDPR'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Streaming', 'Content', 'Publishing', 'Gaming', 'Music', 'Film', 'TV', 'News', 'Social Media', 'Podcast'],
    problems: [
      { id: 'med-001', name: 'Content Recommendation', icon: '🎯', problemTypes: ['recommendation', 'personalization'], dataTypes: ['behavioral', 'content'], priority: 1 },
      { id: 'med-002', name: 'Content Generation', icon: '✨', problemTypes: ['generation'], dataTypes: ['text', 'image', 'audio'], priority: 2 },
      { id: 'med-003', name: 'Sentiment Analysis', icon: '😊', problemTypes: ['sentiment', 'nlp'], dataTypes: ['text', 'social'], priority: 2 },
      { id: 'med-004', name: 'Video Analysis', icon: '🎬', problemTypes: ['vision', 'detection'], dataTypes: ['video'], priority: 3 }
    ]
  },

  government: {
    id: 'government',
    name: 'Government & Public Sector',
    icon: '🏛️',
    description: 'Public services, defense',
    compliance: ['FedRAMP', 'FISMA'],
    dataSensitivity: 'critical',
    subIndustries: ['Federal', 'State', 'Local', 'Defense', 'Intelligence', 'Public Safety', 'Healthcare', 'Education', 'Transportation', 'Utilities'],
    problems: [
      { id: 'gov-001', name: 'Citizen Service Automation', icon: '💬', problemTypes: ['chatbot', 'nlp'], dataTypes: ['text', 'document'], priority: 2 },
      { id: 'gov-002', name: 'Fraud Detection', icon: '🚨', problemTypes: ['anomaly', 'fraud'], dataTypes: ['transaction', 'structured'], priority: 1 },
      { id: 'gov-003', name: 'Public Safety Monitoring', icon: '📡', problemTypes: ['monitoring', 'detection'], dataTypes: ['video', 'sensor'], priority: 1 },
      { id: 'gov-004', name: 'Document Analysis', icon: '📄', problemTypes: ['nlp', 'extraction'], dataTypes: ['document', 'text'], priority: 2 }
    ]
  },

  construction: {
    id: 'construction',
    name: 'Construction & Real Estate',
    icon: '🏗️',
    description: 'Building, infrastructure',
    compliance: ['OSHA', 'IBC'],
    dataSensitivity: 'internal',
    subIndustries: ['Commercial', 'Residential', 'Infrastructure', 'Renovation', 'Prefab', 'Modular', 'Green Building', 'Heavy Civil', 'Demolition', 'Inspection'],
    problems: [
      { id: 'con-001', name: 'Safety Monitoring', icon: '🛡️', problemTypes: ['monitoring', 'detection'], dataTypes: ['video', 'sensor'], priority: 1 },
      { id: 'con-002', name: 'Project Scheduling', icon: '📅', problemTypes: ['scheduling', 'optimization'], dataTypes: ['structured', 'time-series'], priority: 2 },
      { id: 'con-003', name: 'Cost Estimation', icon: '💰', problemTypes: ['prediction', 'regression'], dataTypes: ['structured', 'historical'], priority: 2 }
    ]
  },

  hospitality: {
    id: 'hospitality',
    name: 'Hospitality & Tourism',
    icon: '🏨',
    description: 'Hotels, restaurants, travel',
    compliance: ['PCI-DSS', 'GDPR'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Hotels', 'Restaurants', 'Travel', 'Airlines', 'Cruise', 'Resorts', 'Event', 'Casino', 'Tour', 'Vacation Rental'],
    problems: [
      { id: 'hos-001', name: 'Booking Optimization', icon: '📅', problemTypes: ['optimization', 'forecasting'], dataTypes: ['time-series', 'structured'], priority: 2 },
      { id: 'hos-002', name: 'Customer Personalization', icon: '👤', problemTypes: ['recommendation', 'personalization'], dataTypes: ['behavioral', 'customer'], priority: 2 },
      { id: 'hos-003', name: 'Sentiment Analysis', icon: '😊', problemTypes: ['sentiment', 'nlp'], dataTypes: ['text', 'social'], priority: 3 }
    ]
  },

  professional: {
    id: 'professional',
    name: 'Professional Services',
    icon: '💼',
    description: 'Legal, consulting, accounting',
    compliance: ['SOC2', 'GDPR'],
    dataSensitivity: 'confidential',
    subIndustries: ['Legal', 'Consulting', 'Accounting', 'Audit', 'Tax', 'HR', 'Marketing', 'PR', 'Research', 'Advisory'],
    problems: [
      { id: 'pro-001', name: 'Document Analysis', icon: '📄', problemTypes: ['nlp', 'extraction'], dataTypes: ['document', 'text'], priority: 1 },
      { id: 'pro-002', name: 'Contract Review', icon: '📋', problemTypes: ['nlp', 'classification'], dataTypes: ['document', 'legal'], priority: 1 },
      { id: 'pro-003', name: 'Client Matching', icon: '🤝', problemTypes: ['matching', 'recommendation'], dataTypes: ['structured', 'customer'], priority: 3 }
    ]
  },

  sports: {
    id: 'sports',
    name: 'Sports & Fitness',
    icon: '⚽',
    description: 'Analytics, wearables, training',
    compliance: ['GDPR'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Team Sports', 'Individual', 'Fitness', 'Wearables', 'Training', 'Analytics', 'Fantasy', 'Betting', 'Rehab', 'Coaching'],
    problems: [
      { id: 'spt-001', name: 'Performance Analysis', icon: '📊', problemTypes: ['analysis', 'prediction'], dataTypes: ['sensor', 'video'], priority: 2 },
      { id: 'spt-002', name: 'Injury Prediction', icon: '🏥', problemTypes: ['prediction', 'classification'], dataTypes: ['sensor', 'time-series'], priority: 1 },
      { id: 'spt-003', name: 'Training Optimization', icon: '💪', problemTypes: ['optimization', 'recommendation'], dataTypes: ['sensor', 'behavioral'], priority: 2 }
    ]
  },

  automotive: {
    id: 'automotive',
    name: 'Automotive',
    icon: '🚗',
    description: 'Vehicles, autonomous, telematics',
    compliance: ['ISO 26262', 'NHTSA'],
    dataSensitivity: 'internal',
    subIndustries: ['OEM', 'Suppliers', 'Autonomous', 'Telematics', 'EV', 'Aftermarket', 'Dealership', 'Fleet', 'Motorsport', 'Car Sharing'],
    problems: [
      { id: 'aut-001', name: 'Autonomous Driving', icon: '🚗', problemTypes: ['vision', 'reinforcement'], dataTypes: ['video', 'sensor'], priority: 1 },
      { id: 'aut-002', name: 'Predictive Maintenance', icon: '🔧', problemTypes: ['prediction', 'anomaly'], dataTypes: ['sensor', 'time-series'], priority: 1 },
      { id: 'aut-003', name: 'Quality Control', icon: '✅', problemTypes: ['vision', 'detection'], dataTypes: ['image', 'video'], priority: 2 }
    ]
  },

  insurance: {
    id: 'insurance',
    name: 'Insurance',
    icon: '🛡️',
    description: 'Underwriting, claims, fraud',
    compliance: ['SOC2', 'GDPR'],
    dataSensitivity: 'critical',
    subIndustries: ['Life', 'Health', 'Property', 'Casualty', 'Auto', 'Reinsurance', 'Cyber', 'Travel', 'Pet', 'Commercial'],
    problems: [
      { id: 'ins-001', name: 'Underwriting Automation', icon: '📋', problemTypes: ['classification', 'risk'], dataTypes: ['structured', 'financial'], priority: 1 },
      { id: 'ins-002', name: 'Claims Processing', icon: '📄', problemTypes: ['nlp', 'extraction'], dataTypes: ['document', 'text'], priority: 1 },
      { id: 'ins-003', name: 'Fraud Detection', icon: '🚨', problemTypes: ['anomaly', 'fraud'], dataTypes: ['transaction', 'structured'], priority: 1 },
      { id: 'ins-004', name: 'Risk Assessment', icon: '⚖️', problemTypes: ['prediction', 'risk'], dataTypes: ['structured', 'historical'], priority: 2 }
    ]
  },

  pharma: {
    id: 'pharma',
    name: 'Pharmaceuticals',
    icon: '💊',
    description: 'Drug discovery, clinical trials',
    compliance: ['FDA', 'GxP'],
    dataSensitivity: 'critical',
    subIndustries: ['Research', 'Development', 'Clinical', 'Manufacturing', 'Distribution', 'Regulatory', 'Quality', 'R&D', 'Biologics', 'Generic'],
    problems: [
      { id: 'pha-001', name: 'Drug Discovery', icon: '🔬', problemTypes: ['generation', 'prediction'], dataTypes: ['chemical', 'genomic'], priority: 1 },
      { id: 'pha-002', name: 'Clinical Trial Optimization', icon: '🧪', problemTypes: ['optimization', 'matching'], dataTypes: ['structured', 'medical'], priority: 2 },
      { id: 'pha-003', name: 'Quality Control', icon: '✅', problemTypes: ['vision', 'detection'], dataTypes: ['image', 'sensor'], priority: 1 }
    ]
  },

  environmental: {
    id: 'environmental',
    name: 'Environmental',
    icon: '🌍',
    description: 'Climate, sustainability',
    compliance: ['EPA', 'ISO 14001'],
    dataSensitivity: 'internal',
    subIndustries: ['Climate', 'Waste', 'Recycling', 'Water', 'Air Quality', 'Carbon', 'Biodiversity', 'Conservation', 'Monitoring', 'Sustainability'],
    problems: [
      { id: 'env-001', name: 'Climate Prediction', icon: '🌤️', problemTypes: ['forecasting', 'prediction'], dataTypes: ['weather', 'time-series'], priority: 1 },
      { id: 'env-002', name: 'Pollution Detection', icon: '⚠️', problemTypes: ['detection', 'monitoring'], dataTypes: ['sensor', 'satellite'], priority: 1 },
      { id: 'env-003', name: 'Waste Optimization', icon: '♻️', problemTypes: ['optimization', 'classification'], dataTypes: ['image', 'sensor'], priority: 2 }
    ]
  },

  legal: {
    id: 'legal',
    name: 'Legal',
    icon: '⚖️',
    description: 'Document analysis, compliance',
    compliance: ['GDPR', 'Attorney-Client'],
    dataSensitivity: 'confidential',
    subIndustries: ['Law Firms', 'Corporate', 'Courts', 'Patent', 'Litigation', 'Compliance', 'Regulatory', 'Contract', 'IP', 'eDiscovery'],
    problems: [
      { id: 'lgl-001', name: 'Contract Analysis', icon: '📄', problemTypes: ['nlp', 'extraction'], dataTypes: ['document', 'text'], priority: 1 },
      { id: 'lgl-002', name: 'Legal Research', icon: '🔍', problemTypes: ['search', 'nlp'], dataTypes: ['document', 'text'], priority: 1 },
      { id: 'lgl-003', name: 'Case Prediction', icon: '📊', problemTypes: ['classification', 'prediction'], dataTypes: ['structured', 'historical'], priority: 3 }
    ]
  },

  aerospace: {
    id: 'aerospace',
    name: 'Aerospace & Defense',
    icon: '✈️',
    description: 'Flight systems, satellites',
    compliance: ['FAA', 'ITAR'],
    dataSensitivity: 'critical',
    subIndustries: ['Commercial', 'Defense', 'Satellite', 'Space', 'UAV', 'Maintenance', 'Simulation', 'Navigation', 'Communication', 'Surveillance'],
    problems: [
      { id: 'aer-001', name: 'Flight Prediction', icon: '✈️', problemTypes: ['prediction', 'forecasting'], dataTypes: ['time-series', 'sensor'], priority: 2 },
      { id: 'aer-002', name: 'Satellite Monitoring', icon: '🛰️', problemTypes: ['monitoring', 'detection'], dataTypes: ['satellite', 'image'], priority: 2 },
      { id: 'aer-003', name: 'Maintenance Prediction', icon: '🔧', problemTypes: ['prediction', 'anomaly'], dataTypes: ['sensor', 'time-series'], priority: 1 }
    ]
  },

  crossindustry: {
    id: 'crossindustry',
    name: 'Cross-Industry & Generic',
    icon: '🌐',
    description: 'General enterprise solutions',
    compliance: ['SOC2', 'GDPR'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Enterprise', 'SMB', 'Startup', 'Non-profit', 'Public', 'Private', 'B2B', 'B2C', 'Internal', 'External'],
    problems: [
      { id: 'x-001', name: 'Business Process Automation', icon: '🤖', problemTypes: ['automation', 'optimization'], dataTypes: ['structured', 'document'], priority: 1 },
      { id: 'x-002', name: 'Data Analytics', icon: '📊', problemTypes: ['analysis', 'segmentation'], dataTypes: ['structured', 'unstructured'], priority: 1 },
      { id: 'x-003', name: 'Customer Service', icon: '💬', problemTypes: ['chatbot', 'nlp'], dataTypes: ['text', 'conversational'], priority: 2 },
      { id: 'x-004', name: 'Document Processing', icon: '📄', problemTypes: ['nlp', 'extraction'], dataTypes: ['document', 'text'], priority: 2 },
      { id: 'x-005', name: 'Forecasting', icon: '🔮', problemTypes: ['forecasting', 'prediction'], dataTypes: ['time-series', 'structured'], priority: 2 }
    ]
  },

  // ============================================================
  // INDUSTRIES 24-40 (ADDITIONAL)
  // ============================================================

  logistics: {
    id: 'logistics',
    name: 'Logistics & Supply Chain',
    icon: '📦',
    description: 'End-to-end supply chain management',
    compliance: ['ISO 28000', 'CBP'],
    dataSensitivity: 'internal',
    subIndustries: ['Freight Forwarding', 'Last Mile', 'Cold Chain', 'Cross-border', 'Reverse Logistics', 'Warehouse Automation', 'Inventory Management', 'Order Fulfillment', 'Customs Brokerage', 'Distribution'],
    problems: [
      {
        id: 'log-001',
        name: 'Real-time Shipment Tracking',
        icon: '📡',
        problemTypes: ['monitoring', 'tracking', 'prediction'],
        dataTypes: ['gps', 'sensor', 'streaming'],
        priority: 1,
        useCases: [
          { id: 'log-001-uc1', name: 'Live Package Tracking', description: 'Real-time package location', stakeholders: ['Customer', 'Logistics Manager'], successCriteria: 'Real-time updates', components: ['GPS', 'IoT Sensors', 'Real-time DB'] }
        ]
      },
      {
        id: 'log-002',
        name: 'Warehouse Automation',
        icon: '🏭',
        problemTypes: ['automation', 'optimization', 'vision'],
        dataTypes: ['video', 'sensor', 'inventory'],
        priority: 1,
        useCases: [
          { id: 'log-002-uc1', name: 'Automated Picking', description: 'Robot-assisted picking', stakeholders: ['Warehouse Manager', 'Worker'], successCriteria: 'Increase speed by 50%', components: ['Robotics', 'Vision', 'WMS'] }
        ]
      },
      {
        id: 'log-003',
        name: 'Cold Chain Monitoring',
        icon: '❄️',
        problemTypes: ['monitoring', 'anomaly', 'prediction'],
        dataTypes: ['sensor', 'time-series', 'streaming'],
        priority: 1,
        useCases: [
          { id: 'log-003-uc1', name: 'Temperature Monitoring', description: 'Monitor cold chain', stakeholders: ['Quality Manager'], successCriteria: 'Zero breaches', components: ['IoT Sensors', 'Alert System', 'TSDB'] }
        ]
      }
    ]
  },

  foodBeverage: {
    id: 'food-beverage',
    name: 'Food & Beverage',
    icon: '🍽️',
    description: 'Food production, restaurants, delivery',
    compliance: ['FDA', 'HACCP'],
    dataSensitivity: 'internal',
    subIndustries: ['Restaurants', 'Food Delivery', 'Meal Kits', 'Beverage', 'Breweries', 'Wineries', 'Food Processing', 'Packaging', 'Catering', 'Ghost Kitchens'],
    problems: [
      {
        id: 'fb-001',
        name: 'Food Quality Inspection',
        icon: '🔍',
        problemTypes: ['vision', 'detection', 'quality'],
        dataTypes: ['image', 'video', 'sensor'],
        priority: 1,
        useCases: [
          { id: 'fb-001-uc1', name: 'Visual Food Inspection', description: 'Detect food defects', stakeholders: ['Quality Manager'], successCriteria: '95% defect detection', components: ['CNN', 'Camera', 'Quality Model'] }
        ]
      },
      {
        id: 'fb-002',
        name: 'Demand Forecasting for Restaurants',
        icon: '📈',
        problemTypes: ['forecasting', 'prediction'],
        dataTypes: ['time-series', 'structured', 'weather'],
        priority: 1,
        useCases: [
          { id: 'fb-002-uc1', name: 'Daily Demand Prediction', description: 'Predict daily demand', stakeholders: ['Restaurant Manager', 'Chef'], successCriteria: '90% accuracy', components: ['Time Series', 'Forecasting', 'POS Data'] }
        ]
      }
    ]
  },

  realEstate: {
    id: 'real-estate',
    name: 'Real Estate & PropTech',
    icon: '🏠',
    description: 'Property management, valuation, smart buildings',
    compliance: ['GDPR', 'Fair Housing'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Residential', 'Commercial', 'Property Management', 'REIT', 'PropTech', 'Smart Buildings', 'Facility Management', 'Co-working', 'Short-term Rental', 'Mortgage'],
    problems: [
      {
        id: 're-001',
        name: 'Property Valuation',
        icon: '💰',
        problemTypes: ['regression', 'prediction', 'forecasting'],
        dataTypes: ['structured', 'geospatial', 'market'],
        priority: 1,
        useCases: [
          { id: 're-001-uc1', name: 'Automated Valuation', description: 'Automated property valuation', stakeholders: ['Appraiser', 'Investor'], successCriteria: '95% accuracy', components: ['Regression', 'Property Data', 'Market Data'] }
        ]
      },
      {
        id: 're-002',
        name: 'Smart Building Management',
        icon: '🏢',
        problemTypes: ['monitoring', 'optimization', 'prediction'],
        dataTypes: ['sensor', 'time-series', 'energy'],
        priority: 1,
        useCases: [
          { id: 're-002-uc1', name: 'Energy Optimization', description: 'Optimize building energy', stakeholders: ['Facility Manager'], successCriteria: 'Reduce energy by 30%', components: ['IoT Sensors', 'Optimization', 'Building Data'] }
        ]
      }
    ]
  },

  hr: {
    id: 'hr',
    name: 'HR & Recruitment',
    icon: '👔',
    description: 'Talent acquisition, employee management',
    compliance: ['EEOC', 'GDPR'],
    dataSensitivity: 'confidential',
    subIndustries: ['Recruitment', 'Talent Management', 'Payroll', 'Benefits', 'Performance', 'L&D', 'Engagement', 'Workforce Planning', 'Onboarding', 'Offboarding'],
    problems: [
      {
        id: 'hr-001',
        name: 'Resume Screening',
        icon: '📄',
        problemTypes: ['nlp', 'extraction', 'ranking'],
        dataTypes: ['document', 'text', 'structured'],
        priority: 1,
        useCases: [
          { id: 'hr-001-uc1', name: 'Automated Screening', description: 'Screen resumes', stakeholders: ['Recruiter'], successCriteria: 'Reduce time by 70%', components: ['NLP', 'Resume Parser', 'Matching'] }
        ]
      },
      {
        id: 'hr-002',
        name: 'Employee Attrition Prediction',
        icon: '👋',
        problemTypes: ['classification', 'prediction', 'churn'],
        dataTypes: ['structured', 'behavioral', 'hr'],
        priority: 1,
        useCases: [
          { id: 'hr-002-uc1', name: 'Attrition Prediction', description: 'Predict employee departure', stakeholders: ['HR Manager'], successCriteria: '85% accuracy', components: ['ML Model', 'Employee Data', 'Prediction'] }
        ]
      }
    ]
  },

  cybersecurity: {
    id: 'cybersecurity',
    name: 'Cybersecurity',
    icon: '🔒',
    description: 'Security operations, threat intelligence',
    compliance: ['ISO 27001', 'NIST', 'SOC2'],
    dataSensitivity: 'critical',
    subIndustries: ['SOC', 'Threat Intelligence', 'Pen Testing', 'Compliance', 'IAM', 'Network Security', 'Endpoint Security', 'Cloud Security', 'App Security', 'Incident Response'],
    problems: [
      {
        id: 'sec-001',
        name: 'Threat Detection & Response',
        icon: '🚨',
        problemTypes: ['anomaly', 'detection', 'classification'],
        dataTypes: ['log', 'network', 'streaming'],
        priority: 1,
        useCases: [
          { id: 'sec-001-uc1', name: 'Intrusion Detection', description: 'Detect intrusions', stakeholders: ['Security Analyst', 'CISO'], successCriteria: '99% detection', components: ['IDS', 'ML Model', 'Alert System'] }
        ]
      },
      {
        id: 'sec-002',
        name: 'Phishing Detection',
        icon: '🎣',
        problemTypes: ['classification', 'nlp', 'detection'],
        dataTypes: ['text', 'email', 'url'],
        priority: 1,
        useCases: [
          { id: 'sec-002-uc1', name: 'Email Phishing', description: 'Detect phishing emails', stakeholders: ['Security Team'], successCriteria: '97% detection', components: ['NLP', 'Email Analysis', 'Threat DB'] }
        ]
      }
    ]
  },

  gaming: {
    id: 'gaming',
    name: 'Gaming & Esports',
    icon: '🎮',
    description: 'Game development, esports analytics',
    compliance: ['COPPA', 'GDPR'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Game Dev', 'Esports', 'Mobile Gaming', 'Console', 'PC Gaming', 'Streaming', 'Game Analytics', 'In-game Economy', 'AR/VR Gaming', 'Casual Games'],
    problems: [
      {
        id: 'gam-001',
        name: 'Player Behavior Analysis',
        icon: '📊',
        problemTypes: ['segmentation', 'analysis', 'prediction'],
        dataTypes: ['behavioral', 'game', 'time-series'],
        priority: 1,
        useCases: [
          { id: 'gam-001-uc1', name: 'Player Segmentation', description: 'Segment players', stakeholders: ['Game Designer', 'Marketing'], successCriteria: 'Improve engagement by 25%', components: ['Clustering', 'Player Data', 'Analytics'] }
        ]
      },
      {
        id: 'gam-002',
        name: 'Player Churn Prediction',
        icon: '👋',
        problemTypes: ['classification', 'churn', 'prediction'],
        dataTypes: ['behavioral', 'game', 'time-series'],
        priority: 1,
        useCases: [
          { id: 'gam-002-uc1', name: 'Player Churn', description: 'Predict player departure', stakeholders: ['Game Producer'], successCriteria: '85% accuracy', components: ['ML Model', 'Player History', 'Churn Model'] }
        ]
      }
    ]
  },

  nonprofit: {
    id: 'nonprofit',
    name: 'Non-Profit & Social Impact',
    icon: '🤝',
    description: 'Charity, social services, impact',
    compliance: ['GDPR', 'IRS'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Charity', 'Social Services', 'Disaster Relief', 'Education', 'Healthcare', 'Environment', 'Human Rights', 'Poverty', 'Community', 'Impact Investing'],
    problems: [
      {
        id: 'npo-001',
        name: 'Donor Prediction',
        icon: '💰',
        problemTypes: ['classification', 'prediction', 'segmentation'],
        dataTypes: ['structured', 'behavioral', 'customer'],
        priority: 2,
        useCases: [
          { id: 'npo-001-uc1', name: 'Donor Segmentation', description: 'Segment donors', stakeholders: ['Fundraising Manager'], successCriteria: 'Increase donations by 25%', components: ['Clustering', 'Donor Data', 'Analytics'] }
        ]
      }
    ]
  },

  adtech: {
    id: 'adtech',
    name: 'Advertising & Marketing Tech',
    icon: '📢',
    description: 'Digital advertising, programmatic',
    compliance: ['GDPR', 'CCPA', 'COPPA'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Programmatic', 'SEM', 'Social Media Marketing', 'Email Marketing', 'Content Marketing', 'Influencer', 'Affiliate', 'Marketing Automation', 'CDP', 'Attribution'],
    problems: [
      {
        id: 'ad-001',
        name: 'Real-time Bidding Optimization',
        icon: '⚡',
        problemTypes: ['optimization', 'prediction', 'reinforcement'],
        dataTypes: ['streaming', 'behavioral', 'market'],
        priority: 1,
        useCases: [
          { id: 'ad-001-uc1', name: 'Bid Optimization', description: 'Optimize RTB', stakeholders: ['Advertiser', 'Platform'], successCriteria: 'Increase ROI by 30%', components: ['RL', 'RTB Platform', 'Real-time Processing'] }
        ]
      },
      {
        id: 'ad-002',
        name: 'Ad Fraud Detection',
        icon: '🚨',
        problemTypes: ['anomaly', 'fraud', 'classification'],
        dataTypes: ['clickstream', 'log', 'streaming'],
        priority: 1,
        useCases: [
          { id: 'ad-002-uc1', name: 'Click Fraud Detection', description: 'Detect fraudulent clicks', stakeholders: ['Advertiser'], successCriteria: '95% detection', components: ['Anomaly Detection', 'Click Analysis', 'ML Model'] }
        ]
      }
    ]
  },

  marketplace: {
    id: 'marketplace',
    name: 'E-Commerce Marketplace',
    icon: '🏪',
    description: 'Multi-vendor marketplaces',
    compliance: ['PCI-DSS', 'GDPR'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Multi-vendor', 'B2B', 'C2C', 'Dropshipping', 'Subscription', 'Social Commerce', 'Live Shopping', 'Cross-border', 'Digital Goods', 'Services'],
    problems: [
      {
        id: 'mkt-001',
        name: 'Seller Ranking & Trust',
        icon: '⭐',
        problemTypes: ['ranking', 'scoring', 'classification'],
        dataTypes: ['structured', 'behavioral', 'review'],
        priority: 1,
        useCases: [
          { id: 'mkt-001-uc1', name: 'Seller Trust Score', description: 'Calculate trust scores', stakeholders: ['Buyer', 'Platform'], successCriteria: 'Improve trust by 30%', components: ['Ranking Model', 'Review Analysis', 'Trust Engine'] }
        ]
      }
    ]
  },

  streaming: {
    id: 'streaming',
    name: 'Streaming & OTT',
    icon: '🎬',
    description: 'Video streaming, music streaming',
    compliance: ['DMCA', 'GDPR'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Video Streaming', 'Music Streaming', 'Live Streaming', 'Podcast', 'Audiobook', 'Sports Streaming', 'News Streaming', 'Gaming Streaming', 'Educational', 'Corporate'],
    problems: [
      {
        id: 'str-001',
        name: 'Content Recommendation Engine',
        icon: '🎯',
        problemTypes: ['recommendation', 'personalization', 'ranking'],
        dataTypes: ['behavioral', 'content', 'time-series'],
        priority: 1,
        useCases: [
          { id: 'str-001-uc1', name: 'Video Recommendations', description: 'Recommend videos', stakeholders: ['Viewer', 'Platform'], successCriteria: 'Increase watch time by 40%', components: ['Recommendation Engine', 'User Behavior', 'Content DB'] }
        ]
      },
      {
        id: 'str-002',
        name: 'Live Stream Moderation',
        icon: '🛡️',
        problemTypes: ['detection', 'classification', 'vision'],
        dataTypes: ['video', 'streaming', 'audio'],
        priority: 1,
        useCases: [
          { id: 'str-002-uc1', name: 'Live Moderation', description: 'Moderate live streams', stakeholders: ['Moderator'], successCriteria: 'Real-time moderation', components: ['Vision Model', 'Real-time Processing', 'Alert System'] }
        ]
      }
    ]
  },

  iot: {
    id: 'iot',
    name: 'IoT & Connected Devices',
    icon: '📡',
    description: 'Smart devices, sensors',
    compliance: ['GDPR', 'ISO 27001'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Smart Home', 'Wearables', 'Industrial IoT', 'Connected Cars', 'Smart Cities', 'Healthcare IoT', 'Agricultural IoT', 'Environmental IoT', 'Smart Appliances', 'Fleet IoT'],
    problems: [
      {
        id: 'iot-001',
        name: 'Device Anomaly Detection',
        icon: '⚠️',
        problemTypes: ['anomaly', 'detection', 'prediction'],
        dataTypes: ['sensor', 'streaming', 'time-series'],
        priority: 1,
        useCases: [
          { id: 'iot-001-uc1', name: 'Device Monitoring', description: 'Monitor device health', stakeholders: ['Device Manager'], successCriteria: '95% detection', components: ['IoT Platform', 'Anomaly Detection', 'Alert System'] }
        ]
      }
    ]
  },

  blockchain: {
    id: 'blockchain',
    name: 'Blockchain & Web3',
    icon: '⛓️',
    description: 'DeFi, crypto, smart contracts',
    compliance: ['SEC', 'AML', 'KYC'],
    dataSensitivity: 'critical',
    subIndustries: ['Cryptocurrency', 'DeFi', 'NFT', 'Smart Contracts', 'DAO', 'dApps', 'Blockchain Analytics', 'Crypto Exchange', 'Wallet', 'Tokenization'],
    problems: [
      {
        id: 'blk-001',
        name: 'Crypto Fraud Detection',
        icon: '🚨',
        problemTypes: ['anomaly', 'fraud', 'detection'],
        dataTypes: ['transaction', 'graph', 'streaming'],
        priority: 1,
        useCases: [
          { id: 'blk-001-uc1', name: 'Transaction Fraud', description: 'Detect fraud', stakeholders: ['Exchange', 'User'], successCriteria: '95% detection', components: ['Graph Analysis', 'Anomaly Detection', 'Transaction DB'] }
        ]
      }
    ]
  },

  healthInsurance: {
    id: 'health-insurance',
    name: 'Healthcare Insurance',
    icon: '🏥',
    description: 'Health insurance, claims',
    compliance: ['HIPAA', 'ACA'],
    dataSensitivity: 'critical',
    subIndustries: ['Health Insurance', 'Dental', 'Vision', 'Medicare', 'Medicaid', 'Group', 'Individual', 'Supplemental', 'Long-term Care', 'Disability'],
    problems: [
      {
        id: 'hi-001',
        name: 'Claims Adjudication Automation',
        icon: '📋',
        problemTypes: ['classification', 'nlp', 'extraction'],
        dataTypes: ['document', 'medical', 'structured'],
        priority: 1,
        useCases: [
          { id: 'hi-001-uc1', name: 'Auto Claims Processing', description: 'Automate claims', stakeholders: ['Claims Processor'], successCriteria: 'Reduce by 70%', components: ['NLP', 'Claims Engine', 'Medical Coding'] }
        ]
      }
    ]
  },

  aviation: {
    id: 'aviation',
    name: 'Aviation & Airlines',
    icon: '✈️',
    description: 'Airline operations',
    compliance: ['FAA', 'ICAO'],
    dataSensitivity: 'critical',
    subIndustries: ['Commercial Airlines', 'Cargo', 'Airports', 'ATC', 'Maintenance', 'Ground Handling', 'Crew Management', 'Revenue Management', 'Flight Operations', 'Airspace'],
    problems: [
      {
        id: 'avi-001',
        name: 'Flight Delay Prediction',
        icon: '⏰',
        problemTypes: ['prediction', 'classification', 'forecasting'],
        dataTypes: ['time-series', 'weather', 'structured'],
        priority: 1,
        useCases: [
          { id: 'avi-001-uc1', name: 'Delay Prediction', description: 'Predict delays', stakeholders: ['Airline', 'Passenger'], successCriteria: '90% accuracy', components: ['ML Model', 'Weather Data', 'Flight Data'] }
        ]
      }
    ]
  },

  robotics: {
    id: 'robotics',
    name: 'Robotics & Automation',
    icon: '🤖',
    description: 'Robot control, automation',
    compliance: ['ISO 10218', 'OSHA'],
    dataSensitivity: 'internal',
    subIndustries: ['Industrial Robots', 'Cobots', 'Service Robots', 'AMR', 'Robot Vision', 'Robot Control', 'Pick & Place', 'Assembly', 'Welding', 'Inspection'],
    problems: [
      {
        id: 'rob-001',
        name: 'Robot Vision & Perception',
        icon: '👁️',
        problemTypes: ['vision', 'detection', 'segmentation'],
        dataTypes: ['video', 'image', '3d'],
        priority: 1,
        useCases: [
          { id: 'rob-001-uc1', name: 'Object Detection', description: 'Detect objects', stakeholders: ['Robot Engineer'], successCriteria: '95% accuracy', components: ['CNN', 'Depth Camera', 'Vision System'] }
        ]
      }
    ]
  },

  pharmaRetail: {
    id: 'pharma-retail',
    name: 'Pharmacy & Retail Health',
    icon: '💊',
    description: 'Retail pharmacy',
    compliance: ['HIPAA', 'DEA'],
    dataSensitivity: 'critical',
    subIndustries: ['Retail Pharmacy', 'Mail-order', 'Specialty', 'Compounding', 'Tele-pharmacy', 'PBM', 'Adherence', 'Prescription Management', 'OTC Retail', 'Health Clinics'],
    problems: [
      {
        id: 'phr-001',
        name: 'Drug Interaction Detection',
        icon: '⚠️',
        problemTypes: ['classification', 'detection', 'risk'],
        dataTypes: ['medical', 'structured', 'drug'],
        priority: 1,
        useCases: [
          { id: 'phr-001-uc1', name: 'Drug Interaction Check', description: 'Detect interactions', stakeholders: ['Pharmacist', 'Patient'], successCriteria: '99% detection', components: ['Drug Database', 'ML Model', 'Alert System'] }
        ]
      }
    ]
  },

  smartCity: {
    id: 'smart-city',
    name: 'Smart City & Urban Tech',
    icon: '🏙️',
    description: 'City infrastructure',
    compliance: ['GDPR'],
    dataSensitivity: 'sensitive',
    subIndustries: ['Traffic Management', 'Public Transit', 'Waste Management', 'Water Management', 'Public Safety', 'Parking', 'Street Lighting', 'Air Quality', 'Noise Monitoring', 'Emergency Response'],
    problems: [
      {
        id: 'sc-001',
        name: 'Traffic Flow Optimization',
        icon: '🚦',
        problemTypes: ['optimization', 'prediction', 'routing'],
        dataTypes: ['geospatial', 'sensor', 'time-series'],
        priority: 1,
        useCases: [
          { id: 'sc-001-uc1', name: 'Traffic Optimization', description: 'Optimize traffic', stakeholders: ['City Planner'], successCriteria: 'Reduce congestion by 30%', components: ['Optimization', 'Traffic Sensors', 'Control System'] }
        ]
      }
    ]
  }
};

// ============================================================
// UNIVERSAL PROBLEM TYPES (40)
// ============================================================

export const UNIVERSAL_PROBLEM_TYPES = [
  { id: 'classification', name: 'Classification', icon: '🏷️', description: 'Categorize data into classes' },
  { id: 'regression', name: 'Regression', icon: '📈', description: 'Predict continuous values' },
  { id: 'forecasting', name: 'Forecasting', icon: '🔮', description: 'Predict future values' },
  { id: 'anomaly', name: 'Anomaly Detection', icon: '⚠️', description: 'Identify unusual patterns' },
  { id: 'recommendation', name: 'Recommendation', icon: '🎯', description: 'Suggest relevant items' },
  { id: 'nlp', name: 'NLP', icon: '💬', description: 'Process natural language' },
  { id: 'vision', name: 'Computer Vision', icon: '👁️', description: 'Analyze images/video' },
  { id: 'generation', name: 'Generation', icon: '✨', description: 'Create new content' },
  { id: 'segmentation', name: 'Segmentation', icon: '🧩', description: 'Group similar items' },
  { id: 'optimization', name: 'Optimization', icon: '⚙️', description: 'Find optimal solutions' },
  { id: 'sentiment', name: 'Sentiment Analysis', icon: '😊', description: 'Analyze opinions' },
  { id: 'ranking', name: 'Ranking', icon: '📊', description: 'Order items' },
  { id: 'chatbot', name: 'Chatbot', icon: '🤖', description: 'Conversational AI' },
  { id: 'search', name: 'Search/Retrieval', icon: '🔍', description: 'Find information' },
  { id: 'extraction', name: 'Information Extraction', icon: '📋', description: 'Extract structured data' },
  { id: 'translation', name: 'Translation', icon: '🌐', description: 'Convert languages' },
  { id: 'summarization', name: 'Summarization', icon: '📝', description: 'Condense content' },
  { id: 'reinforcement', name: 'Reinforcement Learning', icon: '🎮', description: 'Learn through interaction' },
  { id: 'planning', name: 'Planning/Scheduling', icon: '📅', description: 'Optimize allocation' },
  { id: 'detection', name: 'Detection/Tracking', icon: '🎯', description: 'Identify objects' },
  { id: 'diagnosis', name: 'Diagnosis', icon: '🩺', description: 'Identify conditions' },
  { id: 'fraud', name: 'Fraud Detection', icon: '🚨', description: 'Detect fraudulent activity' },
  { id: 'churn', name: 'Churn Prediction', icon: '👋', description: 'Predict customer departure' },
  { id: 'risk', name: 'Risk Assessment', icon: '⚖️', description: 'Evaluate risks' },
  { id: 'quality', name: 'Quality Control', icon: '✅', description: 'Ensure quality standards' },
  { id: 'maintenance', name: 'Predictive Maintenance', icon: '🔧', description: 'Predict equipment failure' },
  { id: 'monitoring', name: 'Monitoring', icon: '📡', description: 'Track and monitor' },
  { id: 'automation', name: 'Automation', icon: '🤖', description: 'Automate processes' },
  { id: 'personalization', name: 'Personalization', icon: '👤', description: 'Customize experiences' },
  { id: 'matching', name: 'Matching', icon: '🤝', description: 'Match entities' },
  { id: 'routing', name: 'Routing', icon: '🗺️', description: 'Optimize routes' },
  { id: 'scheduling', name: 'Scheduling', icon: '📅', description: 'Optimize schedules' },
  { id: 'clustering', name: 'Clustering', icon: '🔄', description: 'Group similar items' },
  { id: 'time-series', name: 'Time Series', icon: '📈', description: 'Analyze temporal data' },
  { id: 'simulation', name: 'Simulation', icon: '🔬', description: 'Simulate scenarios' },
  { id: 'scoring', name: 'Scoring', icon: '📊', description: 'Score and rank' },
  { id: 'conversational', name: 'Conversational', icon: '💬', description: 'Conversational AI' },
  { id: 'analysis', name: 'Analysis', icon: '📊', description: 'Analyze data' },
  { id: 'prediction', name: 'Prediction', icon: '🔮', description: 'Predict outcomes' },
  { id: 'tracking', name: 'Tracking', icon: '📍', description: 'Track objects/movement' }
];

// ============================================================
// UNIVERSAL DATA TYPES (43)
// ============================================================

export const UNIVERSAL_DATA_TYPES = [
  { id: 'structured', name: 'Structured Data', icon: '📊', description: 'Tables, databases' },
  { id: 'unstructured', name: 'Unstructured Data', icon: '📄', description: 'Documents, files' },
  { id: 'image', name: 'Images', icon: '🖼️', description: 'Photos, scans' },
  { id: 'video', name: 'Video', icon: '🎬', description: 'Video streams' },
  { id: 'audio', name: 'Audio', icon: '🎵', description: 'Sound, speech' },
  { id: 'text', name: 'Text', icon: '📝', description: 'Documents, messages' },
  { id: 'sensor', name: 'Sensor/IoT', icon: '📡', description: 'Device data' },
  { id: 'time-series', name: 'Time Series', icon: '📈', description: 'Temporal data' },
  { id: 'geospatial', name: 'Geospatial', icon: '🗺️', description: 'Maps, location' },
  { id: 'graph', name: 'Graph/Network', icon: '🕸️', description: 'Relationships' },
  { id: 'streaming', name: 'Streaming', icon: '🌊', description: 'Real-time data' },
  { id: 'genomic', name: 'Genomic', icon: '🧬', description: 'DNA sequences' },
  { id: 'chemical', name: 'Chemical', icon: '⚗️', description: 'Molecular structures' },
  { id: 'biometric', name: 'Biometric', icon: '👆', description: 'Fingerprints, facial' },
  { id: 'financial', name: 'Financial', icon: '💰', description: 'Transactions, market' },
  { id: 'medical', name: 'Medical', icon: '🏥', description: 'Clinical data' },
  { id: 'behavioral', name: 'Behavioral', icon: '👤', description: 'User behavior' },
  { id: 'social', name: 'Social Media', icon: '📱', description: 'Posts, engagement' },
  { id: 'log', name: 'Log Data', icon: '📋', description: 'System logs' },
  { id: 'document', name: 'Documents', icon: '📄', description: 'Contracts, reports' },
  { id: 'satellite', name: 'Satellite Imagery', icon: '🛰️', description: 'Space imagery' },
  { id: 'weather', name: 'Weather Data', icon: '🌤️', description: 'Meteorological' },
  { id: 'market', name: 'Market Data', icon: '📊', description: 'Stock, commodity' },
  { id: 'customer', name: 'Customer Data', icon: '👥', description: 'Customer info' },
  { id: 'transaction', name: 'Transaction Data', icon: '💳', description: 'Purchases' },
  { id: 'inventory', name: 'Inventory Data', icon: '📦', description: 'Stock levels' },
  { id: 'equipment', name: 'Equipment Data', icon: '⚙️', description: 'Machine data' },
  { id: 'energy', name: 'Energy Data', icon: '⚡', description: 'Consumption' },
  { id: 'environmental', name: 'Environmental', icon: '🌍', description: 'Climate' },
  { id: 'legal', name: 'Legal Data', icon: '⚖️', description: 'Legal documents' },
  { id: 'educational', name: 'Educational', icon: '📚', description: 'Learning data' },
  { id: 'agricultural', name: 'Agricultural', icon: '🌾', description: 'Farming data' },
  { id: 'telecom', name: 'Telecom Data', icon: '📡', description: 'Network data' },
  { id: 'media', name: 'Media Data', icon: '🎬', description: 'Content data' },
  { id: 'game', name: 'Game Data', icon: '🎮', description: 'Gaming data' },
  { id: 'supply-chain', name: 'Supply Chain', icon: '🚚', description: 'Logistics' },
  { id: 'hr', name: 'HR Data', icon: '👔', description: 'Employee data' },
  { id: 'real-estate', name: 'Real Estate', icon: '🏠', description: 'Property data' },
  { id: 'code', name: 'Code', icon: '💻', description: 'Source code' },
  { id: 'ticket', name: 'Ticket Data', icon: '🎫', description: 'Support tickets' },
  { id: '3d', name: '3D Data', icon: '📐', description: '3D models' },
  { id: 'process', name: 'Process Data', icon: '⚙️', description: 'Process metrics' },
  { id: 'gps', name: 'GPS Data', icon: '📍', description: 'Location tracking' }
];

export default UNIVERSAL_INDUSTRIES;